import { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePlaidLink } from "react-plaid-link";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Loader2, CheckCircle2, ArrowRight, Shield, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const [step, setStep] = useState("intro");
  const [loading, setLoading] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [isOAuthRedirect, setIsOAuthRedirect] = useState(false);

  useEffect(() => {
    const oauthParam = searchParams.get("oauth");
    const storedOAuthState = localStorage.getItem("plaid_oauth_state_id");
    if (oauthParam === "true" && storedOAuthState) {
      setIsOAuthRedirect(true);
      localStorage.removeItem("plaid_oauth_state_id");
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        const response = await api.post("/plaid/create_link_token");
        if (response.data.link_token) setLinkToken(response.data.link_token);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to initialize bank connection");
      }
    };
    fetchLinkToken();
  }, [api]);

  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setStep("connecting");
    setLoading(true);
    try {
      const response = await api.post("/plaid/exchange_public_token", { public_token: publicToken });
      if (response.data.success) {
        setStep("success");
        const count = response.data.accounts_connected || 0;
        const bank = metadata?.institution?.name || "your bank";
        toast.success(count > 0 ? `Connected ${count} account${count !== 1 ? "s" : ""} from ${bank}!` : `Connected to ${bank}. Syncing shortly.`);
        setTimeout(() => navigate("/"), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect bank. Please try again.");
      setStep("intro");
    } finally {
      setLoading(false);
    }
  }, [api, navigate]);

  const plaidConfig = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: () => { setStep("intro"); setIsOAuthRedirect(false); },
    onEvent: (e, m) => console.log("Plaid event:", e, m),
  };
  if (isOAuthRedirect) plaidConfig.receivedRedirectUri = window.location.href;

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink(plaidConfig);

  useEffect(() => {
    if (isOAuthRedirect && plaidReady && linkToken) openPlaidLink();
  }, [isOAuthRedirect, plaidReady, linkToken, openPlaidLink]);

  const handleConnectPlaid = () => {
    if (linkToken && plaidReady) openPlaidLink();
    else toast.error("Bank connection is loading. Please try again.");
  };

  const handleConfirmSkip = async () => {
    setShowSkipDialog(false);
    try { await api.post("/plaid/skip-onboarding"); } catch {}
    toast.info("You can connect your bank later from Settings");
    navigate("/");
  };

  return (
    <div className="tally-onboarding">
      <style>{`
        .tally-onboarding { min-height:100vh; display:flex; font-family:'Hanken Grotesk',-apple-system,sans-serif; background:#FBF9F3; color:#23241E; }
        .tally-onb-left { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px 24px; }
        .tally-onb-right { flex:1; display:none; position:relative; background:#14432B; overflow:hidden; }
        @media(min-width:1024px){ .tally-onb-right{display:flex;align-items:center;justify-content:center;} }
        .tally-onb-right .orb1 { position:absolute; width:500px; height:500px; border-radius:50%; background:radial-gradient(circle,rgba(126,217,87,.25),transparent 70%); top:-100px; right:-100px; }
        .tally-onb-right .orb2 { position:absolute; width:350px; height:350px; border-radius:50%; background:radial-gradient(circle,rgba(63,125,82,.35),transparent 70%); bottom:-80px; left:-80px; }
        .tally-onb-right .msg { position:relative; z-index:2; max-width:360px; text-align:center; color:#F4EFE4; padding:0 24px; }
        .tally-onb-right .msg h2 { font-family:'Lora',Georgia,serif; font-size:26px; font-weight:600; margin-bottom:14px; line-height:1.3; }
        .tally-onb-right .msg p { font-size:15px; line-height:1.7; opacity:.8; }
        .tally-onb-content { width:100%; max-width:420px; }
        .tally-onb-content .logo { display:flex; align-items:center; justify-content:center; gap:12px; margin-bottom:40px; text-decoration:none; color:inherit; }
        .tally-onb-content .logo img { height:36px; }
        .tally-onb-card { background:#fff; border:1.5px solid #E8E2D5; border-radius:20px; padding:36px 32px; box-shadow:0 2px 10px rgba(20,67,43,.04); }
        .tally-onb-card h1 { font-family:'Lora',Georgia,serif; font-size:24px; font-weight:600; margin:0 0 8px; text-align:center; }
        .tally-onb-card .sub { font-size:15px; color:#6f6f64; text-align:center; margin-bottom:28px; line-height:1.5; }
        .tally-onb-card .features { display:flex; flex-direction:column; gap:14px; margin-bottom:28px; }
        .tally-onb-card .feat { display:flex; align-items:flex-start; gap:12px; }
        .tally-onb-card .feat-icon { width:40px; height:40px; border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .tally-onb-card .feat h3 { font-size:14px; font-weight:700; margin:0 0 2px; }
        .tally-onb-card .feat p { font-size:13px; color:#6f6f64; margin:0; line-height:1.4; }
        .btn-connect {
          width:100%; height:52px; border:none; border-radius:50px; font-size:16px; font-weight:700;
          background:#14432B; color:#F4EFE4; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:8px;
          transition:background .2s,transform .1s; font-family:inherit;
        }
        .btn-connect:hover { background:#1a5838; }
        .btn-connect:active { transform:scale(.98); }
        .btn-connect:disabled { opacity:.5; cursor:not-allowed; }
        .btn-skip {
          width:100%; height:48px; border:1.5px solid #E8E2D5; border-radius:50px; font-size:15px; font-weight:600;
          background:transparent; color:#6f6f64; cursor:pointer; margin-top:12px; transition:all .2s; font-family:inherit;
        }
        .btn-skip:hover { border-color:#14432B; color:#23241E; }
        .tally-onb-card .legal { text-align:center; font-size:12px; color:#6f6f64; margin-top:20px; }
        .tally-onb-card .legal a { color:#3F7D52; text-decoration:none; }
        .tally-onb-card .legal a:hover { text-decoration:underline; }
        .tally-onb-card .trust { text-align:center; font-size:12px; color:#6f6f64; margin-top:16px; }
        .status-card { text-align:center; padding:48px 32px; }
        .status-icon { width:64px; height:64px; border-radius:50%; display:flex; align-items:center; justify-content:center; margin:0 auto 20px; }
        .status-card h2 { font-family:'Lora',Georgia,serif; font-size:22px; font-weight:600; margin-bottom:8px; }
        .status-card p { font-size:15px; color:#6f6f64; }
        @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .fade-in { animation:fadeIn .4s ease-out; }
      `}</style>

      <div className="tally-onb-left">
        <div className="tally-onb-content">
          <Link to="/" className="logo">
            <img src="/tally-logo.png" alt="Tally" />
          </Link>

          {step === "intro" && (
            <div className="tally-onb-card fade-in" data-testid="onboarding-card">
              <h1>Connect your bank</h1>
              <p className="sub">Securely link your accounts and Tally does the rest — automatically tracking your spending, bills, and budget.</p>

              <div className="features">
                <div className="feat">
                  <div className="feat-icon" style={{background:"rgba(126,217,87,.12)"}}>
                    <Shield size={20} style={{color:"#3F7D52"}} />
                  </div>
                  <div>
                    <h3>Bank-grade encryption</h3>
                    <p>256-bit security through Plaid, trusted by millions</p>
                  </div>
                </div>
                <div className="feat">
                  <div className="feat-icon" style={{background:"rgba(59,130,246,.1)"}}>
                    <Eye size={20} style={{color:"#3b82f6"}} />
                  </div>
                  <div>
                    <h3>Read-only access</h3>
                    <p>We can only view transactions — never move your money</p>
                  </div>
                </div>
                <div className="feat">
                  <div className="feat-icon" style={{background:"rgba(245,158,11,.1)"}}>
                    <Lock size={20} style={{color:"#f59e0b"}} />
                  </div>
                  <div>
                    <h3>Your data stays yours</h3>
                    <p>We never sell or share your financial information</p>
                  </div>
                </div>
              </div>

              <button className="btn-connect" onClick={handleConnectPlaid} disabled={!plaidReady || loading || !linkToken} data-testid="connect-plaid-button">
                {!linkToken ? <><Loader2 size={18} className="animate-spin" /> Loading...</> : <>Connect with Plaid <ArrowRight size={18} /></>}
              </button>

              <button className="btn-skip" onClick={() => setShowSkipDialog(true)} data-testid="skip-button">Skip for now</button>

              <p className="legal">
                By connecting, you agree to our <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms of Service</Link>
              </p>
              <p className="trust">Takes about 30 seconds · Works with all major Canadian banks</p>
            </div>
          )}

          {step === "connecting" && (
            <div className="tally-onb-card fade-in">
              <div className="status-card">
                <div className="status-icon" style={{background:"rgba(126,217,87,.12)"}}>
                  <Loader2 size={28} style={{color:"#14432B"}} className="animate-spin" />
                </div>
                <h2>Connecting to your bank</h2>
                <p>Securely fetching your accounts and transactions...</p>
              </div>
            </div>
          )}

          {step === "success" && (
            <div className="tally-onb-card fade-in">
              <div className="status-card">
                <div className="status-icon" style={{background:"rgba(126,217,87,.15)"}}>
                  <CheckCircle2 size={28} style={{color:"#3F7D52"}} />
                </div>
                <h2>Successfully connected!</h2>
                <p>Your accounts are ready. Taking you to your dashboard...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="tally-onb-right">
        <div className="orb1" />
        <div className="orb2" />
        <div className="msg">
          <h2>One step to financial clarity.</h2>
          <p>Once connected, Tally automatically imports your transactions, categorizes spending, and shows you exactly where your money goes.</p>
        </div>
      </div>

      <AlertDialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <AlertDialogContent style={{maxWidth:420,borderRadius:20,border:"1.5px solid #E8E2D5",background:"#fff",fontFamily:"'Hanken Grotesk',-apple-system,sans-serif"}} data-testid="skip-dialog">
          <AlertDialogHeader>
            <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
              <div style={{width:56,height:56,borderRadius:"50%",background:"rgba(126,217,87,.12)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Shield size={26} style={{color:"#3F7D52"}} />
              </div>
            </div>
            <AlertDialogTitle style={{textAlign:"center",fontFamily:"'Lora',Georgia,serif",fontSize:20,fontWeight:600}}>
              Plaid is safe &amp; secure
            </AlertDialogTitle>
            <AlertDialogDescription style={{textAlign:"center"}}>
              <div style={{display:"flex",flexDirection:"column",gap:14,paddingTop:12}}>
                <div style={{display:"flex",alignItems:"flex-start",gap:12,textAlign:"left"}}>
                  <div style={{width:36,height:36,borderRadius:10,background:"rgba(126,217,87,.1)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <Lock size={16} style={{color:"#3F7D52"}} />
                  </div>
                  <div>
                    <p style={{fontWeight:700,fontSize:13,color:"#23241E",margin:"0 0 2px"}}>Bank-grade security</p>
                    <p style={{fontSize:12,color:"#6f6f64",margin:0}}>256-bit encryption, the same level banks use</p>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"flex-start",gap:12,textAlign:"left"}}>
                  <div style={{width:36,height:36,borderRadius:10,background:"rgba(59,130,246,.08)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <Eye size={16} style={{color:"#3b82f6"}} />
                  </div>
                  <div>
                    <p style={{fontWeight:700,fontSize:13,color:"#23241E",margin:"0 0 2px"}}>Read-only access</p>
                    <p style={{fontSize:12,color:"#6f6f64",margin:0}}>We can only view transactions — never move money</p>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"flex-start",gap:12,textAlign:"left"}}>
                  <div style={{width:36,height:36,borderRadius:10,background:"rgba(245,158,11,.08)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <EyeOff size={16} style={{color:"#f59e0b"}} />
                  </div>
                  <div>
                    <p style={{fontWeight:700,fontSize:13,color:"#23241E",margin:"0 0 2px"}}>No data without connection</p>
                    <p style={{fontSize:12,color:"#6f6f64",margin:0}}>Skip means no real account balances or transactions</p>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter style={{display:"flex",flexDirection:"column",gap:8}}>
            <button className="btn-connect" onClick={() => { setShowSkipDialog(false); handleConnectPlaid(); }} data-testid="dialog-connect-button">
              <Shield size={16} /> Connect with Plaid
            </button>
            <button className="btn-skip" onClick={handleConfirmSkip} style={{marginTop:0}} data-testid="dialog-skip-button">
              Skip for now
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
